'use client'

import Link from 'next/link'
import { useEffect, useState, type ReactNode } from 'react'
import { CloseIcon, MenuIcon } from '@/components/ui/icons'
import { HashScrollLink } from './HashScrollLink'

type MobileNavLink = {
  href: string
  label: string
  live?: boolean
}

export function MobileNav({ links, authLink }: { links: MobileNavLink[]; authLink: ReactNode }) {
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (!open) return

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }

    document.addEventListener('keydown', closeOnEscape)
    return () => document.removeEventListener('keydown', closeOnEscape)
  }, [open])

  return (
    <div className="relative lg:hidden">
      <button
        type="button"
        aria-expanded={open}
        aria-controls="mobile-site-navigation"
        aria-label={open ? 'Chiudi menu' : 'Apri menu'}
        onClick={() => setOpen((current) => !current)}
        className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-md border border-[color:var(--league-line)] bg-[color:var(--league-surface)] text-[color:var(--league-ink)]"
      >
        <span aria-hidden className="inline-flex">
          {open ? <CloseIcon className="h-5 w-5" /> : <MenuIcon className="h-5 w-5" />}
        </span>
      </button>

      {open && (
        <div
          id="mobile-site-navigation"
          className="absolute right-0 top-[calc(100%+0.5rem)] z-[var(--z-dropdown)] w-[min(18rem,calc(100vw-2rem))] rounded-2xl border border-[color:var(--league-line)] bg-[color:var(--league-surface)] p-3 shadow-[var(--league-shadow-lg)]"
        >
          <nav aria-label="Navigazione principale" className="flex flex-col gap-1">
            {links.map((link) => {
              const itemClass =
                'flex min-h-11 items-center rounded-lg px-3 text-[16px] font-medium text-[color:var(--league-ink)] hover:bg-[color:var(--league-surface-alt)]'
              if (link.href.includes('#')) {
                return (
                  <HashScrollLink
                    key={link.label}
                    href={link.href}
                    onNavigate={() => setOpen(false)}
                    className={itemClass}
                  >
                    {link.label}
                  </HashScrollLink>
                )
              }
              return (
                <Link key={link.label} href={link.href} onClick={() => setOpen(false)} className={itemClass}>
                  {link.label}
                  {link.live && (
                    <span className="relative ml-2 flex h-2 w-2" aria-label="In diretta">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
                    </span>
                  )}
                </Link>
              )
            })}
          </nav>
          <div className="mt-2 border-t border-[color:var(--league-line)] pt-2" onClick={() => setOpen(false)}>
            {authLink}
          </div>
        </div>
      )}
    </div>
  )
}
